import { useSyncExternalStore } from 'react';
import Button from '@mui/material/Button';
import OpenSeadragon from 'openseadragon';

let enabled = false;
const viewers = new Map();
const listeners = new Set();

let snapshot = { enabled, viewerCount: 0 };

const updateSnapshot = () => {
  snapshot = { enabled, viewerCount: viewers.size };
};

const emit = () => {
  updateSnapshot();
  listeners.forEach((listener) => listener());
};

export const getSyncViewSnapshot = () => snapshot;

export const subscribeSyncView = (listener) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

export const getSyncViewEnabled = () => enabled;

const resetSyncReferences = () => {
  viewers.forEach((controller) => controller.resetReference());
};

export const setSyncViewEnabled = (next) => {
  if (!!next === enabled) return;

  enabled = !!next;
  resetSyncReferences();
  emit();
};

export const toggleSyncView = () => setSyncViewEnabled(!enabled);

export const registerSyncViewer = (id, controller) => {
  viewers.set(id, controller);
  resetSyncReferences();
  emit();
};

export const unregisterSyncViewer = (id) => {
  viewers.delete(id);
  emit();
};

export const broadcastViewState = (sourceId, viewState) => {
  if (!enabled) return;

  viewers.forEach((controller, id) => {
    if (id === sourceId) return;
    controller.applyViewState(viewState);
  });
};

export const createSyncViewerController = ({ id, viewer }) => {
  const controller = {
    id,
    viewer,
    lastAppliedState: null,
    referenceState: null,
    referenceSource: null,
    isApplying: false,

    getState() {
      if (!this.viewer?.viewport || !this.viewer?.source) return null;

      const center = this.viewer.viewport.getCenter(true);
      const contentFactor = this.viewer.world.getContentFactor();
      const zoom = this.viewer.viewport.getZoom(true);

      if (!Number.isFinite(contentFactor) || contentFactor <= 0) return null;

      return {
        // OSD viewport coordinates are normalized by image width. Use image
        // pixels so differently sized slides move the same distance at the
        // synchronized zoomFactor.
        x: center.x * contentFactor,
        y: center.y * contentFactor,
        zoomFactor: zoom / contentFactor,
      };
    },

    resetReference() {
      this.referenceState = this.getState();
      this.referenceSource = this.viewer?.source;
      this.lastAppliedState = this.referenceState;
    },

    isSameState(stateA, stateB) {
      if (!stateA || !stateB) return false;

      const centerDelta =
        Math.abs(stateA.x - stateB.x) + Math.abs(stateA.y - stateB.y);
      const zoomDelta = Math.abs(stateA.zoomFactor - stateB.zoomFactor);

      return (
        centerDelta < 1e-6 &&
        zoomDelta < Math.abs(stateB.zoomFactor) * 1e-6 + 1e-12
      );
    },

    applyViewState(viewState) {
      const target = this.viewer;

      if (
        !target?.viewport ||
        !target?.source ||
        !this.referenceState ||
        target.source !== this.referenceSource
      ) {
        return;
      }

      const targetState = {
        ...viewState,
        x: this.referenceState.x + viewState.x,
        y: this.referenceState.y + viewState.y,
      };

      // Already synchronized with the broadcast view state.
      if (this.isSameState(this.getState(), targetState)) return;

      this.lastAppliedState = targetState;
      this.isApplying = true;

      const contentFactor = target.world.getContentFactor();
      const zoom = targetState.zoomFactor * contentFactor;
      try {
        target.viewport.panTo(
          new OpenSeadragon.Point(
            targetState.x / contentFactor,
            targetState.y / contentFactor,
          ),
          true,
        );
        target.viewport.zoomTo(zoom, null, true);
      } catch (e) {
        // Ignore view state application errors while syncing.
      } finally {
        this.isApplying = false;
      }
    },

    handleViewportChange() {
      if (!getSyncViewEnabled() || this.isApplying) return;

      const state = this.getState();
      if (!state) return;

      // A newly opened/replaced slide establishes its own position. Rebase the
      // group together so its initial viewport never moves the other slides.
      if (!this.referenceState || this.viewer.source !== this.referenceSource) {
        resetSyncReferences();

        return;
      }

      if (this.isSameState(state, this.lastAppliedState)) return;

      this.lastAppliedState = state;
      // Share displacement from the alignment chosen when sync was enabled,
      // never the absolute coordinates of another slide.
      broadcastViewState(this.id, {
        ...state,
        x: state.x - this.referenceState.x,
        y: state.y - this.referenceState.y,
      });
    },
  };

  return controller;
};

export const useSyncViewStore = () =>
  useSyncExternalStore(
    subscribeSyncView,
    getSyncViewSnapshot,
    getSyncViewSnapshot,
  );

export const SyncViewToggle = () => {
  const { enabled: isEnabled, viewerCount } = useSyncViewStore();

  // Sync only makes sense when more than one viewer is open.
  if (viewerCount < 2) return null;

  return (
    <Button
      id="sync-view-toggle"
      variant={isEnabled ? 'contained' : 'outlined'}
      color={isEnabled ? 'primary' : 'inherit'}
      size="small"
      onClick={toggleSyncView}
    >
      {isEnabled ? 'Sync Controls: On' : 'Sync Controls: Off'}
    </Button>
  );
};
